// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useState, useEffect, useMemo } from 'react';
import { useRestaurant } from '../../contexts/RestaurantContext.tsx';
import { ChevronDown, ChevronUp, AlertTriangle, X } from 'lucide-react';
import { toast } from 'react-toastify';
import TableEditor from './TableEditor.tsx';
import { calculateTableDimensions } from '../../constants.ts';
import { fetchMesaOcupacoes } from '../../services/api.ts';
import '../../styles/room-layout-settings.css';
import Tooltip from '@mui/material/Tooltip/Tooltip';
import IconButton from '@mui/material/IconButton/IconButton';
import { Edit, Delete } from '@mui/icons-material';

interface RoomLayoutSettingsProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
  isFocused?: boolean;
  usarMapaMesas?: boolean;
}

interface TableGroup {
  min_pessoas: number;
  max_pessoas: number;
  quantidade: number;
}

const RoomLayoutSettings: React.FC<RoomLayoutSettingsProps> = ({
  isCollapsed = false,
  onToggle,
  isFocused = false,
  usarMapaMesas = true,
}) => {
  const { floors, currentFloor, updateFloor, addTable, deleteTable } = useRestaurant();
  const currentFloorData = floors.find(f => f.id === currentFloor);
  const collapsed = isFocused ? false : isCollapsed;

  const [modoLayout, setModoLayout] = useState(false);
  const [savingMapMode, setSavingMapMode] = useState(false);
  const [minPessoas, setMinPessoas] = useState(1);
  const [maxPessoas, setMaxPessoas] = useState(20);
  const [capacidadeMaxima, setCapacidadeMaxima] = useState<number | ''>('');
  const [batchMin, setBatchMin] = useState(1);
  const [batchMax, setBatchMax] = useState(2);
  const [batchQuantity, setBatchQuantity] = useState(1);
  const [batchNamePattern, setBatchNamePattern] = useState('Mesa {n}');
  const [batchError, setBatchError] = useState('');
  const [isBatchCreating, setIsBatchCreating] = useState(false);
  const [isGroupSaving, setIsGroupSaving] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const [tablePage, setTablePage] = useState(0);
  const [editingTableId, setEditingTableId] = useState<string | null>(null);
  const [deletingTableId, setDeletingTableId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [, setGroupTargets] = useState<Record<string, number>>({});
  const [, setGroupErrors] = useState<Record<string, string>>({});
  const [, setBulkSaveError] = useState('');
  const [bulkSaveSuccess, setBulkSaveSuccess] = useState('');
  const [, setOccupiedTableIds] = useState<Set<string>>(new Set());
  const [, setOccupancyError] = useState('');
  const [, setOccupancyLoading] = useState(false);
  const capacityModeEnabled = !usarMapaMesas || !modoLayout;
  const isTableActionBusy = isBatchCreating || isGroupSaving || Boolean(deletingTableId);

  const tableGroups = useMemo<TableGroup[]>(() => {
    if (!currentFloorData) return [];

    const groups = new Map<string, TableGroup>();
    currentFloorData.tables.forEach((table) => {
      const min = Number(table.minCovers) || 1;
      const max = Number(table.maxCovers) || min;
      const normalizedMin = Math.max(1, Math.floor(min));
      const normalizedMax = Math.max(normalizedMin, Math.floor(max));
      const key = `${normalizedMin}-${normalizedMax}`;
      const existing = groups.get(key) || {
        min_pessoas: normalizedMin,
        max_pessoas: normalizedMax,
        quantidade: 0
      };
      existing.quantidade += 1;
      groups.set(key, existing);
    });

    return Array.from(groups.values()).sort((a, b) => {
      if (a.min_pessoas === b.min_pessoas) return a.max_pessoas - b.max_pessoas;
      return a.min_pessoas - b.min_pessoas;
    });
  }, [currentFloorData]);

  useEffect(() => {
    if (currentFloorData) {
      setModoLayout(currentFloorData.modo_layout !== false);
      setMinPessoas(currentFloorData.min_pessoas_por_reserva || 1);
      setMaxPessoas(currentFloorData.max_pessoas_por_reserva || 20);
      setCapacidadeMaxima(currentFloorData.capacidade_maxima ?? '');
      setBatchMin(1);
      setBatchMax(2);
      setBatchQuantity(1);
      setBatchNamePattern('Mesa {n}');
      setBatchError('');
      setIsBatchCreating(false);
      setIsGroupSaving(false);
      setTableSearch('');
      setTablePage(0);
      setEditingTableId(null);
      setDeletingTableId(null);
      setOccupancyError('');
      setGroupErrors({});
      setBulkSaveError('');
      setBulkSaveSuccess('');
    }
  }, [currentFloorData]);

  useEffect(() => {
    const nextTargets: Record<string, number> = {};
    tableGroups.forEach((group) => {
      const key = `${group.min_pessoas}-${group.max_pessoas}`;
      nextTargets[key] = group.quantidade;
    });
    setGroupTargets(nextTargets);
  }, [tableGroups]);

  useEffect(() => {
    setTablePage(0);
  }, [tableSearch, currentFloor]);

  useEffect(() => {
    if (!bulkSaveSuccess) return;
    const timer = window.setTimeout(() => setBulkSaveSuccess(''), 3000);
    return () => window.clearTimeout(timer);
  }, [bulkSaveSuccess]);

  useEffect(() => {
    if (modoLayout) {
      setEditingTableId(null);
      setDeletingTableId(null);
    }
  }, [modoLayout]);

  useEffect(() => {
    if (!currentFloorData || !usarMapaMesas) {
      setOccupiedTableIds(new Set());
      return;
    }

    let isActive = true;

    const loadOccupancy = async () => {
      setOccupancyLoading(true);
      try {
        const data = await fetchMesaOcupacoes({
          sala_id: currentFloorData.id,
          ativas: true
        });
        if (!isActive) return;
        const ids = new Set(
          (data || [])
            .map((item: any) => item?.mesa_id)
            .filter((mesaId: any) => mesaId !== null && mesaId !== undefined)
            .map((mesaId: any) => String(mesaId))
        );
        setOccupiedTableIds(ids);
        setOccupancyError('');
      } catch (error) {
        if (!isActive) return;
        setOccupancyError('Não foi possível verificar mesas ocupadas.');
        setOccupiedTableIds(new Set());
      } finally {
        if (isActive) {
          setOccupancyLoading(false);
        }
      }
    };

    loadOccupancy();

    return () => {
      isActive = false;
    };
  }, [currentFloorData, usarMapaMesas]);

  const tablePageSize = 8;

  const filteredTables = useMemo(() => {
    const tables = currentFloorData?.tables ?? [];
    const term = tableSearch.trim().toLowerCase();
    if (!term) return tables;
    return tables.filter((table) => String(table.name ?? '').toLowerCase().includes(term));
  }, [currentFloorData, tableSearch]);

  const tablePageCount = Math.max(1, Math.ceil(filteredTables.length / tablePageSize));
  const safeTablePage = Math.min(tablePage, tablePageCount - 1);

  const paginatedTables = useMemo(() => {
    const start = safeTablePage * tablePageSize;
    return filteredTables.slice(start, start + tablePageSize);
  }, [filteredTables, safeTablePage, tablePageSize]);

  const totalTables = currentFloorData?.tables?.length ?? 0;
  const tableEmptyMessage = totalTables === 0 ? 'Nenhuma mesa cadastrada.' : 'Nenhuma mesa encontrada.';
  const tableCountLabel = filteredTables.length === 1 ? '1 mesa' : `${filteredTables.length} mesas`;

  const editingTable = useMemo(() => {
    if (!editingTableId) return null;
    return (currentFloorData?.tables ?? []).find((table) => String(table.id) === String(editingTableId)) ?? null;
  }, [currentFloorData, editingTableId]);

  useEffect(() => {
    if (tablePage > tablePageCount - 1) {
      setTablePage(Math.max(0, tablePageCount - 1));
    }
  }, [tablePage, tablePageCount]);


  const getCapacidadeMaximaValue = () => {
    if (capacidadeMaxima === '') return undefined;

    const parsed = Math.floor(Number(capacidadeMaxima));
    if (!Number.isFinite(parsed)) return undefined;

    return Math.max(1, parsed);
  };

  const handleSave = () => {
    updateFloor(currentFloor, {
      modo_layout: usarMapaMesas ? modoLayout : currentFloorData?.modo_layout,
      min_pessoas_por_reserva: capacityModeEnabled ? minPessoas : undefined,
      max_pessoas_por_reserva: capacityModeEnabled ? maxPessoas : undefined,
      capacidade_maxima: capacityModeEnabled ? getCapacidadeMaximaValue() : undefined
    });
  };

  const handleMapModeChange = async () => {
    if (!currentFloorData || savingMapMode) return;
    setSavingMapMode(true);
    try {
      await updateFloor(currentFloorData.id, {
        usar_mapa_mesas: !usarMapaMesas,
        ...(!usarMapaMesas ? {} : {
          min_pessoas_por_reserva: minPessoas,
          max_pessoas_por_reserva: maxPessoas,
          capacidade_maxima: getCapacidadeMaximaValue(),
        }),
      });
      toast.success('Uso do mapa de mesas atualizado neste ambiente.');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || error?.response?.data?.message
        || 'Não foi possível alterar o modo. Finalize as ocupações ativas deste ambiente e tente novamente.');
    } finally {
      setSavingMapMode(false);
    }
  };

  const normalizeTableNamePattern = (pattern: string) => {
    const trimmed = pattern.trim();
    return trimmed.length > 0 ? trimmed : 'Mesa {n}';
  };

  const buildTableName = (pattern: string, number: number) => {
    const normalized = normalizeTableNamePattern(pattern);
    if (/\{n\}/i.test(normalized)) {
      return normalized.replace(/\{n\}/gi, String(number)).trim();
    }
    return `${normalized} ${number}`.trim();
  };

  const getNextTableNumber = () => {
    const existingTables = currentFloorData?.tables ?? [];
    let maxNumber = 0;
    existingTables.forEach((table) => {
      const match = /(\d+)\s*$/.exec(String(table.name ?? '').trim());
      if (match) {
        const value = parseInt(match[1], 10);
        if (!Number.isNaN(value)) {
          maxNumber = Math.max(maxNumber, value);
        }
      }
    });
    if (maxNumber === 0) {
      return existingTables.length + 1;
    }
    return maxNumber + 1;
  };

  const createPlacementState = () => {
    const floorWidth = currentFloorData?.width || 800;
    const padding = 40;
    const gap = 20;
    const existingTables = currentFloorData?.tables ?? [];
    const existingBottom = existingTables.reduce((max, table) => {
      const tableBottom = (table.position?.y ?? 0) + (table.shape?.height ?? 0) / 2;
      return Math.max(max, tableBottom);
    }, 0);

    return {
      cursorX: padding,
      cursorY: Math.max(padding, existingBottom + gap),
      rowHeight: 0,
      floorWidth,
      padding,
      gap
    };
  };

  const placeTable = (state: ReturnType<typeof createPlacementState>, width: number, height: number) => {
    if (state.cursorX + width > state.floorWidth - state.padding) {
      state.cursorX = state.padding;
      state.cursorY += state.rowHeight + state.gap;
      state.rowHeight = 0;
    }

    const position = {
      x: state.cursorX + width / 2,
      y: state.cursorY + height / 2
    };

    state.cursorX += width + state.gap;
    state.rowHeight = Math.max(state.rowHeight, height);

    return position;
  };

  const createTablesForGroup = async (
    min: number,
    max: number,
    quantity: number,
    options: { namePattern?: string; startNumber?: number } = {}
  ): Promise<number> => {
    if (!currentFloorData) return options.startNumber ?? getNextTableNumber();
    const placementState = createPlacementState();
    let tableNumber = Number.isFinite(options.startNumber)
      ? Number(options.startNumber)
      : getNextTableNumber();
    const namePattern = options.namePattern ?? batchNamePattern;
    const tablesToCreate: any[] = [];

    for (let i = 0; i < quantity; i += 1) {
      let { width, height } = calculateTableDimensions('square', max);
      if (width < 60) width = 60;
      if (height < 60) height = 60;

      tablesToCreate.push({
        name: buildTableName(namePattern, tableNumber),
        shape: { type: 'square', width, height },
        texture: 'light-wood',
        position: placeTable(placementState, width, height),
        minCovers: min,
        maxCovers: max,
        rotation: 0,
        textColor: '#000000',
        chairColor: '#8B4513',
        color: '#8B4513',
        useColor: false
      });

      tableNumber += 1;
    }

    const results = await Promise.all(tablesToCreate.map((table) => addTable(table)));
    if (results.some((created) => !created)) {
      throw new Error('Falha ao criar uma ou mais mesas.');
    }
    return tableNumber;
  };

  const handleCreateBatch = async () => {
    if (!currentFloorData || isTableActionBusy) return;
    const min = Math.max(1, Math.floor(batchMin || 1));
    const maxCandidate = Math.floor(batchMax || min);
    const max = Math.max(min, maxCandidate);
    const quantity = Math.max(1, Math.floor(batchQuantity || 1));

    if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(quantity)) {
      setBatchError('Valores inválidos para criar mesas.');
      return;
    }

    setBatchError('');
    setIsBatchCreating(true);
    try {
      await createTablesForGroup(min, max, quantity, { namePattern: batchNamePattern });
      setBatchMin(min);
      setBatchMax(max);
      setBatchQuantity(1);
      toast.success('Mesas criadas com sucesso.');
    } catch (error) {
      console.error('Erro ao criar mesas em lote:', error);
      toast.error('Não foi possível criar as mesas.');
    } finally {
      setIsBatchCreating(false);
    }
  };

  const handleDeleteTable = (tableId: string, tableName?: string) => {
    if (isTableActionBusy) return;
    setDeleteTarget({
      id: tableId,
      name: tableName || `Mesa ${tableId}`,
    });
  };

  const confirmDeleteTable = async () => {
    if (!deleteTarget) return;
    const { id: tableId } = deleteTarget;
    setDeletingTableId(tableId);
    try {
      const removed = await deleteTable(tableId);
      if (!removed) {
        toast.error('Não foi possível excluir a mesa.');
        return;
      }
      if (editingTableId === tableId) {
        setEditingTableId(null);
      }
      toast.success('Mesa excluída com sucesso.');
      setDeleteTarget(null);
    } catch (error) {
      console.error('Erro ao excluir mesa:', error);
      toast.error('Não foi possível excluir a mesa.');
    } finally {
      setDeletingTableId(null);
    }
  };

  const cancelDeleteTable = () => {
    if (deletingTableId) return;
    setDeleteTarget(null);
  };

  if (!currentFloorData) return null;

  return (
    <div className={`room-layout-settings ${collapsed ? 'collapsed' : ''} ${isFocused ? 'focused bare' : ''}`}>
      {!isFocused && (
        <div className="selector-header" onClick={onToggle}>
        <h3>Configurações do ambiente</h3>
          <button className="toggle-btn">
            {collapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
          </button>
        </div>
      )}
      {!collapsed && (
        <div className="settings-content">
          <section className="settings-section">
            <div className="settings-section-content">
              <div className="room-map-mode">
                <div>
                  <label htmlFor="room-use-table-map">Usar mapa de mesas neste ambiente</label>
                  <p>Quando desativado, o atendimento usa a quantidade de pessoas e a capacidade máxima deste ambiente.</p>
                  {!usarMapaMesas && <p>Configure a capacidade máxima antes de sentar clientes.</p>}
                </div>
                <input
                  id="room-use-table-map"
                  type="checkbox"
                  role="switch"
                  checked={usarMapaMesas}
                  disabled={savingMapMode}
                  onChange={handleMapModeChange}
                />
              </div>
            </div>
          </section>


          {capacityModeEnabled && (
            <>
              <section className="settings-section">
                <div className="settings-section-header">
                  <div>
                    <h4>{'Configura\u00e7\u00f5es do ambiente'}</h4>
                  </div>
                </div>

                <div className="settings-section-content settings-grid">
              <div className="form-group">
                <label htmlFor="min-pessoas">Mínimo de pessoas por reserva</label>
                <input
                  id="min-pessoas"
                  type="number"
                  min="1"
                  max="50"
                  value={minPessoas}
                  onChange={(e) => setMinPessoas(parseInt(e.target.value) || 1)}
                  onBlur={handleSave}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="max-pessoas">Máximo de pessoas por reserva</label>
                <input
                  id="max-pessoas"
                  type="number"
                  min="1"
                  max="100"
                  value={maxPessoas}
                  onChange={(e) => setMaxPessoas(parseInt(e.target.value) || 20)}
                  onBlur={handleSave}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="capacidade-maxima">Capacidade máxima pessoas no ambiente</label>
                <input
                  id="capacidade-maxima"
                  type="number"
                  min="1"
                  max="500"
                  value={capacidadeMaxima}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value === '') {
                      setCapacidadeMaxima('');
                      return;
                    }

                    const parsed = parseInt(value, 10);
                    setCapacidadeMaxima(Number.isNaN(parsed) ? '' : parsed);
                  }}
                  onBlur={handleSave}
                  className="form-control"
                />
                <small className="field-help">
                  {usarMapaMesas
                    ? 'Clientes não poderão fazer reservas se a capacidade máxima for atingida. Deixe em branco para não limitar.'
                    : 'Obrigatória no modo sem mapa para calcular cadeiras ocupadas e disponíveis.'}
                </small>
              </div>

                </div>
              </section>

              {usarMapaMesas && !modoLayout && (
              <section className="settings-section">
                <div className="settings-section-header">
                  <div>
                    <h4>{'Configura\u00e7\u00f5es de mesas'}</h4>
                    <p>{'Crie mesas, ajuste a distribuição por capacidade e acompanhe o cadastro do ambiente.'}</p>
                  </div>
                </div>

                <div className="settings-section-content settings-section-content--tables">
              <div className="table-configs">
                <div className="table-configs-header">
                  <div>
                    <h5>Cadastro e distribuição</h5>
                    <p>
                      {tableGroups.length > 0
                        ? `${totalTables} ${totalTables === 1 ? 'mesa organizada' : 'mesas organizadas'} em ${tableGroups.length} ${tableGroups.length === 1 ? 'faixa' : 'faixas'} de capacidade.`
                        : 'Crie a primeira faixa de mesas para começar a distribuição do ambiente.'}
                    </p>
                  </div>
                </div>

                <div className="table-config-row table-config-row--create">
                  <div className="table-config-fields table-config-fields--batch">
                    <div className="form-group">
                      <label htmlFor="batch-min">Mín pessoas</label>
                      <input
                        id="batch-min"
                        type="number"
                        min="1"
                        max="50"
                        value={batchMin}
                        disabled={isTableActionBusy}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10) || 1;
                          setBatchMin(value);
                          if (batchMax < value) {
                            setBatchMax(value);
                          }
                        }}
                        className="form-control"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="batch-max">Máx pessoas</label>
                      <input
                        id="batch-max"
                        type="number"
                        min={batchMin}
                        max="100"
                        value={batchMax}
                        disabled={isTableActionBusy}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10) || batchMin;
                          setBatchMax(Math.max(batchMin, value));
                        }}
                        className="form-control"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="batch-qty">Mesas</label>
                      <input
                        id="batch-qty"
                        type="number"
                        min="1"
                        max="200"
                        value={batchQuantity}
                        disabled={isTableActionBusy}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10);
                          const normalized = Number.isNaN(value) ? 1 : Math.min(200, Math.max(1, value));
                          setBatchQuantity(normalized);
                        }}
                        className="form-control table-qty-input"
                      />
                    </div>
                    <div className="form-group table-config-field--pattern">
                      <label htmlFor="batch-name-pattern">Prefixo das mesas</label>
                      <div className="name-prefix-input">
                        <input
                          id="batch-name-pattern"
                          type="text"
                          value={batchNamePattern.replace(/\{n\}/gi, '')}
                          disabled={isTableActionBusy}
                          onChange={(e) => setBatchNamePattern(e.target.value)}
                          className="form-control"
                          placeholder="Ex: Mesa, Salão A, Janela…"
                        />
                        <span className="name-prefix-suffix" aria-hidden="true">+ nº</span>
                      </div>
                      {(() => {
                        const cleanPrefix = batchNamePattern.replace(/\{n\}/gi, '').trim() || 'Mesa';
                        const nextNum = getNextTableNumber();
                        const sample1 = `${cleanPrefix} ${nextNum}`;
                        const sample2 = `${cleanPrefix} ${nextNum + 1}`;
                        const sample3 = `${cleanPrefix} ${nextNum + 2}`;
                        return (
                          <div className="name-pattern-preview">
                            <span className="name-pattern-preview-label">As mesas serão criadas como</span>
                            <span className="name-pattern-preview-samples">
                              <span className="name-pattern-chip">{sample1}</span>
                              <span className="name-pattern-chip">{sample2}</span>
                              <span className="name-pattern-chip">{sample3}</span>
                              <span className="name-pattern-ellipsis">…</span>
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="table-config-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCreateBatch}
                      disabled={isTableActionBusy}
                      aria-busy={isBatchCreating}
                    >
                      <span>{isBatchCreating ? 'Criando...' : 'Criar mesas'}</span>
                    </button>
                  </div>

                  {batchError && (
                    <div style={{ color: 'var(--error)', marginTop: 'var(--spacing-xs)' }}>
                      {batchError}
                    </div>
                  )}
                </div>

                {tableGroups.length === 0 && (
                  <div className="table-config-empty">
                    Nenhuma mesa cadastrada ainda.
                  </div>
                )}



              </div>

                  <div className="table-list-section">
              <div className="table-list-header">
                <h5>Busca de mesas</h5>
                <span className="table-list-count">{tableCountLabel}</span>
              </div>

              <div className="table-list-controls">
                <div className="form-group">
                  <input
                    id="table-search"
                    type="text"
                    value={tableSearch}
                    onChange={(e) => setTableSearch(e.target.value)}
                    className="form-control"
                    placeholder="Ex: Mesa 12"
                  />
                </div>
              </div>

              <div className="table-list">
                <div className="table-list-row table-list-row--header">
                  <span>Nome</span>
                  <span>Capacidade</span>
                  <span className="table-list-actions">Ações</span>
                </div>
                {paginatedTables.length === 0 ? (
                  <div className="table-list-empty">
                    {tableEmptyMessage}
                  </div>
                ) : (
                  paginatedTables.map((table) => {
                    const min = Math.max(1, Math.floor(Number(table.minCovers) || 1));
                    const max = Math.max(min, Math.floor(Number(table.maxCovers) || min));
                    const isDeleting = String(deletingTableId) === String(table.id);
                    return (
                      <div key={table.id} className="table-list-row">
                        <span className="table-list-name">{table.name || `Mesa ${table.id}`}</span>
                        <span className="table-list-capacity">{`${min}-${max} pessoas`}</span>
                        <span className="table-list-actions">                          
                            <Tooltip title="Editar">
                              <IconButton size="small" color="primary" onClick={() => setEditingTableId(table.id)} aria-label={`Editar mesa ${table.name}`}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir">
                              <IconButton size="small" color="error" onClick={() => handleDeleteTable(table.id, table.name)} disabled={isTableActionBusy} aria-busy={isDeleting} aria-label={`Excluir mesa ${table.name}`}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                        </span>
                      </div>
                    );
                  })
                )}
              </div>

              {filteredTables.length > 0 && (
                <div className="table-list-pagination">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setTablePage((prev) => Math.max(0, prev - 1))}
                    disabled={safeTablePage === 0}
                  >
                    Anterior
                  </button>
                  <span>Página {safeTablePage + 1} de {tablePageCount}</span>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setTablePage((prev) => Math.min(tablePageCount - 1, prev + 1))}
                    disabled={safeTablePage >= tablePageCount - 1}
                  >
                    Próxima
                  </button>
                </div>
              )}
                  </div>
                </div>
              </section>
              )}
            </>
          )}
        </div>
      )}
      {usarMapaMesas && !modoLayout && editingTable && (
        <div
          className="room-layout-settings-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setEditingTableId(null)}
        >
          <div
            className="room-layout-settings-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <TableEditor table={editingTable} onSave={() => setEditingTableId(null)} />
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="table-delete-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={cancelDeleteTable}
        >
          <div
            className="table-delete-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="table-delete-modal-close"
              onClick={cancelDeleteTable}
              disabled={Boolean(deletingTableId)}
              aria-label="Fechar"
            >
              <X size={16} />
            </button>
            <div className="table-delete-modal-body">
              <div className="table-delete-modal-icon">
                <AlertTriangle size={20} />
              </div>
              <div className="table-delete-modal-text">
                <h4>Excluir mesa?</h4>
                <p>
                  A mesa <strong>{deleteTarget.name}</strong> será removida do ambiente. Esta ação não pode ser desfeita.
                </p>
              </div>
            </div>
            <div className="table-delete-modal-actions">
              <button
                type="button"
                className="table-delete-modal-btn table-delete-modal-btn-cancel"
                onClick={cancelDeleteTable}
                disabled={Boolean(deletingTableId)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="table-delete-modal-btn table-delete-modal-btn-confirm"
                onClick={confirmDeleteTable}
                disabled={Boolean(deletingTableId)}
              >
                {deletingTableId ? 'Excluindo…' : 'Sim, excluir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomLayoutSettings;
